import React, { useRef } from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import parse from "html-react-parser";
import "./blog-list.scss";
import CategoryFilter from "./CategoryFilter";
import BlogItemListSchema from "./BlogItemListSchema";
import { getBlogPagePath } from "../utils/blogPaths";
import { formatDateFr } from "../utils/formatDateFr";

const BlogList = ({
  posts,
  allPosts = [],
  currentPage = 1,
  totalPages = 1,
  selectedCategory = "all",
}) => {
  const blogListRef = useRef(null);
  const { site } = useStaticQuery(graphql`
    query BlogListSiteUrl {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const filterPosts = allPosts.length > 0 ? allPosts : posts;

  if (!posts || posts.length === 0) {
    return null;
  }

  const scrollToTop = () => {
    if (blogListRef.current) {
      blogListRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const previousPage = currentPage > 1 ? currentPage - 1 : null;
  const nextPage = currentPage < totalPages ? currentPage + 1 : null;

  return (
    <div className="blog-list" ref={blogListRef}>
      <BlogItemListSchema
        posts={posts}
        siteUrl={site.siteMetadata.siteUrl}
      />
      <CategoryFilter
        posts={filterPosts}
        selectedCategory={selectedCategory}
      />
      <div className="inner">
        <div className="grid">
          {posts.map((post) => {
            const featuredImage =
              post.featuredImage?.node?.localFile?.childImageSharp?.fluid;
            const primaryCategory = post.categories?.nodes?.[0];

            return (
              <article key={post.id} className="blog-card">
                <div className="blog-meta">
                  {post.date && (
                    <time className="blog-date" dateTime={post.date}>
                      {formatDateFr(post.date)}
                    </time>
                  )}
                  {primaryCategory && (
                    <Link
                      to={`/blog/categorie/${primaryCategory.slug}/`}
                      className="blog-category"
                    >
                      {primaryCategory.name}
                    </Link>
                  )}
                </div>
                <Link to={`/blog/${post.slug}/`} className="card-link">
                  <div className="image">
                    {featuredImage ? (
                      <img
                        src={featuredImage.src}
                        alt={post.featuredImage.node.altText || post.title}
                      />
                    ) : (
                      <div className="no-image"></div>
                    )}
                  </div>
                  <div className="content">
                    <h2>{parse(post.title)}</h2>
                    <div className="excerpt">{parse(post.excerpt)}</div>
                    <span className="read-more">Lire la suite</span>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>

        {totalPages > 1 && (
          <nav className="pagination" aria-label="Pagination du blog">
            {previousPage ? (
              <Link
                className="pagination-button"
                to={getBlogPagePath({
                  categorySlug:
                    selectedCategory !== "all" ? selectedCategory : null,
                  page: previousPage,
                })}
                onClick={scrollToTop}
                rel="prev"
              >
                Précédent
              </Link>
            ) : (
              <span className="pagination-button disabled">Précédent</span>
            )}

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Link
                key={page}
                className={`pagination-button ${
                  currentPage === page ? "active" : ""
                }`}
                to={getBlogPagePath({
                  categorySlug:
                    selectedCategory !== "all" ? selectedCategory : null,
                  page,
                })}
                onClick={scrollToTop}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </Link>
            ))}

            {nextPage ? (
              <Link
                className="pagination-button"
                to={getBlogPagePath({
                  categorySlug:
                    selectedCategory !== "all" ? selectedCategory : null,
                  page: nextPage,
                })}
                onClick={scrollToTop}
                rel="next"
              >
                Suivant
              </Link>
            ) : (
              <span className="pagination-button disabled">Suivant</span>
            )}
          </nav>
        )}
      </div>
    </div>
  );
};

export default BlogList;
